import { Component, EventEmitter, Input, NgZone, OnChanges, OnDestroy, Output, SimpleChanges } from '@angular/core';
import { SkeletonColumn } from '../list-skeleton/list-skeleton.component';
import moment from 'moment';

import { OrganizationService } from 'src/app/data/service/organization.service';
import { SuggestedCodingService } from 'src/app/data/service/suggested-coding.service';
import {
  TransactionLedgerRow,
  TransactionSortColumn,
  TransactionSortDirection
} from 'src/app/data/model/transaction-ledger';
import { formatMoney, formatSigned, importSourceLabel, isReconciledRow, rowKey } from './transaction-row.util';

/**
 * A day header plus the transactions stamped with that date. Sorted by anything other
 * than the date the rows are not in day order any more, so the whole page becomes one
 * group with no header -- see `regroup`.
 */
interface TransactionDayGroup {
  key: string;
  label: string;
  net: number;
  rows: TransactionLedgerRow[];
}

/**
 * The ledger table, shared by the Transactions screen and a report's coding view (SD-1227),
 * so coding a transaction looks and behaves the same wherever it is done.
 *
 * Presentation only: the host loads the rows, owns the sort and the selection, and decides
 * what a click does. The inputs switch on or off what differs between the two screens -- a
 * report view has no Report column (you are in the report) and no running balance (a report
 * is not a statement), and becomes read-only once it has been sent for approval.
 */
/** SD-1235: the ledger's columns as its loading skeleton draws them (see _ledger-table.scss). */
const LEDGER_SKELETON_COLUMNS: SkeletonColumn[] = [
  { width: '44px', kind: 'check' },
  { width: '124px', kind: 'pill' },
  { width: '96px', kind: 'date' },
  { width: 'minmax(220px, 1fr)', kind: 'entity' },
  { width: '240px', kind: 'text', hideOnMobile: true },
  { width: '180px', kind: 'text', hideOnMobile: true },
  { width: '132px', kind: 'amount' },
  { width: '132px', kind: 'amount', hideOnMobile: true },
];

@Component({
  selector: 'app-transactions-table',
  templateUrl: './transactions-table.component.html',
  styleUrls: ['./transactions-table.component.scss']
})
export class TransactionsTableComponent implements OnChanges, OnDestroy {

  @Input() rows: TransactionLedgerRow[] = [];
  @Input() loading = false;
  @Input() loadingMore = false;

  /** SD-1235: the loading skeleton, in the ledger's own column widths. */
  get skeletonColumns(): SkeletonColumn[] {
    return this.readOnly ? LEDGER_SKELETON_COLUMNS.slice(1) : LEDGER_SKELETON_COLUMNS;
  }

  /** The server's sort. The table only shows it and asks for a new one. */
  @Input() sortBy: TransactionSortColumn = 'date';
  @Input() sortDir: TransactionSortDirection = 'asc';

  /** Ticked transaction ids. The host owns it; the table emits the next value. */
  @Input() selected: { [id: string]: boolean } = {};

  /** Lower-cased ids with a coding request in flight. */
  @Input() codingIds: Set<string> = new Set<string>();

  // ── What differs between screens ─────────────────────────────

  /** The account each row belongs to: while several accounts share one list. */
  @Input() showAccountColumn = false;
  /**
   * The running balance, offered where there is a statement to run down. With several accounts
   * in one list each row's figure is still its own account's running total (the ledger
   * partitions it by account), so it is offered there too -- the user switches it on.
   */
  @Input() showBalanceColumn = true;
  /** Which report a row is in. Off inside a report, which is the answer for every row. */
  @Input() showReportColumn = true;
  /**
   * Who a row is assigned to. Off inside a report, which is already one cardholder's part of
   * it, so every row would name the same person.
   */
  @Input() showAssignedColumn = false;
  /** Where each row came from (Bank feed, Xero, Spreadsheet...): offered on the Transactions screen. */
  @Input() showSourceColumn = false;
  /** The comment badge in the Details cell (report view). */
  @Input() showComments = false;
  /** The paperclip for a matched document's file (report view). */
  @Input() showAttachment = false;
  /**
   * No selection and no coding buttons: a report that has gone for approval is not the
   * submitter's to change. Rows still open the panel, which enforces the same rule.
   */
  @Input() readOnly = false;
  /**
   * Columns the user has switched off in the Transactions screen's column picker, by key:
   * status, txn, coded, details, assigned, amount, balance, account, report. The report view
   * sends none.
   */
  @Input() hiddenColumns: string[] = [];

  /** Shown when nothing is loading and there are no rows. */
  @Input() emptyMessage = 'Nothing here.';

  // ── What the host decides ────────────────────────────────────

  @Output() sortChange = new EventEmitter<TransactionSortColumn>();
  @Output() selectedChange = new EventEmitter<{ [id: string]: boolean }>();
  /** The row was clicked: the reconcile panel. */
  @Output() rowOpen = new EventEmitter<TransactionLedgerRow>();
  /** "Add coding": the coding modal. */
  @Output() codeRow = new EventEmitter<TransactionLedgerRow>();
  /** "Match found": the panel, at the suggestion. */
  @Output() reviewMatch = new EventEmitter<TransactionLedgerRow>();
  /** The user scrolled near the bottom of what is loaded. */
  @Output() loadMore = new EventEmitter<void>();
  @Output() commentsOpen = new EventEmitter<TransactionLedgerRow>();
  @Output() attachmentOpen = new EventEmitter<TransactionLedgerRow>();
  /** SD-1254: accept the Coding Assistant's suggested coding on these rows, as suggested. */
  @Output() acceptSuggestions = new EventEmitter<TransactionLedgerRow[]>();

  groups: TransactionDayGroup[] = [];

  /** SD-1235: ≤768px, where _ledger-table.scss hides Coded to, Details, Balance, Account and Report. */
  isNarrow = false;
  private readonly narrowQuery: MediaQueryList | null =
    typeof window !== 'undefined' && window.matchMedia ? window.matchMedia('(max-width: 768px)') : null;
  private readonly onNarrowChange = (e: MediaQueryListEvent) => this.zone.run(() => this.isNarrow = e.matches);

  constructor(
    private _organizationService: OrganizationService,
    private zone: NgZone,
    public suggestedCoding: SuggestedCodingService
  ) {
    if (this.narrowQuery) {
      this.isNarrow = this.narrowQuery.matches;
      this.narrowQuery.addEventListener?.('change', this.onNarrowChange);
    }
  }

  ngOnDestroy(): void {
    this.narrowQuery?.removeEventListener?.('change', this.onNarrowChange);
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.rows || changes.sortBy) {
      this.regroup();
    }
  }

  // ── Columns ────────────────────────────────────────────────

  /** Whether the user has left this column on. */
  shows(column: string): boolean {
    return !(this.hiddenColumns || []).includes(column);
  }

  get balanceVisible(): boolean {
    return this.showBalanceColumn && this.shows('balance');
  }

  get accountVisible(): boolean {
    return this.showAccountColumn && this.shows('account');
  }

  get reportVisible(): boolean {
    return this.showReportColumn && this.shows('report');
  }

  get assignedVisible(): boolean {
    return this.showAssignedColumn && this.shows('assigned');
  }

  get sourceVisible(): boolean {
    return this.showSourceColumn && this.shows('source');
  }

  sourceLabel(row: TransactionLedgerRow): string {
    return importSourceLabel(row && row.importMethod);
  }

  /** For the day header, which spans the whole row. */
  get columnCount(): number {
    // SD-1235: count only the columns actually drawn. A span wider than the table makes the
    // browser add empty columns, which on a phone (five columns hidden) took most of the width
    // and squeezed the real ones into a third of the screen.
    const narrowHidden = ['coded', 'details'];
    let count = ['status', 'txn', 'coded', 'details', 'amount']
      .filter(c => this.shows(c) && !(this.isNarrow && narrowHidden.includes(c))).length;
    if (!this.readOnly) { count++; }
    if (this.showDateColumn) { count++; }
    if (this.isNarrow) { return count; }
    if (this.assignedVisible) { count++; }
    if (this.balanceVisible) { count++; }
    if (this.accountVisible) { count++; }
    if (this.reportVisible) { count++; }
    if (this.sourceVisible) { count++; }
    return count;
  }

  /**
   * The day header's cells either side of Amount, so its Net sits in the Amount column, over the
   * figures it adds up. Drawn columns only, as columnCount counts them.
   */
  get daySpanBeforeAmount(): number {
    const narrowHidden = ['coded', 'details'];
    let count = ['status', 'txn', 'coded', 'details']
      .filter(c => this.shows(c) && !(this.isNarrow && narrowHidden.includes(c))).length;
    if (!this.readOnly) { count++; }
    if (this.showDateColumn) { count++; }
    if (!this.isNarrow && this.assignedVisible) { count++; }
    return count;
  }

  get daySpanAfterAmount(): number {
    if (this.isNarrow) { return 0; }
    return [this.balanceVisible, this.accountVisible, this.reportVisible, this.sourceVisible].filter(Boolean).length;
  }

  /** Day headers only make sense while the rows are in date order. */
  get showDayHeaders(): boolean {
    return this.sortBy === 'date';
  }

  /** Under the day headers every row's date is already in its header. */
  get showDateColumn(): boolean {
    return !this.showDayHeaders;
  }

  // ── Sorting ────────────────────────────────────────────────

  sortByColumn(column: TransactionSortColumn): void {
    this.sortChange.emit(column);
  }

  isSortedBy(column: TransactionSortColumn): boolean {
    return this.sortBy === column;
  }

  /** For the header's aria-sort, which wants the words rather than asc/desc. */
  ariaSort(column: TransactionSortColumn): string {
    if (this.sortBy !== column) { return 'none'; }

    return this.sortDir === 'asc' ? 'ascending' : 'descending';
  }

  /**
   * Groups the loaded rows by day, preserving the server's order.
   *
   * Only while sorted by date: under any other sort the rows are not in date order, so
   * day headers would break the list into a run of one-row "days" and each header's Net
   * would be a total of that single row. The list is flat instead.
   */
  private regroup(): void {
    const rows = this.rows || [];
    const groups: TransactionDayGroup[] = [];
    const byKey: { [key: string]: TransactionDayGroup } = {};

    if (this.sortBy !== 'date') {
      this.groups = rows.length
        ? [{ key: 'all', label: '', net: 0, rows: rows.slice() }]
        : [];
      return;
    }

    rows.forEach(row => {
      const key = row.transactionDate
        ? moment(row.transactionDate).format('YYYY-MM-DD')
        : 'unknown';

      if (!byKey[key]) {
        byKey[key] = { key, label: this.dayLabel(row.transactionDate), net: 0, rows: [] };
        groups.push(byKey[key]);
      }

      byKey[key].rows.push(row);
      byKey[key].net += row.amount;
    });

    this.groups = groups;
  }

  private dayLabel(value: string | null): string {
    if (!value) { return 'Undated'; }

    const date = moment(value);
    const base = date.format('ddd D MMM YYYY');

    if (date.isSame(moment(), 'day')) { return `Today · ${base}`; }
    if (date.isSame(moment().subtract(1, 'day'), 'day')) { return `Yesterday · ${base}`; }

    return base;
  }

  // ── Row actions ────────────────────────────────────────────

  onRowClick(row: TransactionLedgerRow): void {
    if (this.isRowCoding(row)) { return; }
    this.rowOpen.emit(row);
  }

  onCodeRow(row: TransactionLedgerRow, event: Event): void {
    // The row itself opens the reconcile panel; this is a different destination.
    event.stopPropagation();
    this.codeRow.emit(row);
  }

  onReviewMatch(row: TransactionLedgerRow, event: Event): void {
    event.stopPropagation();
    this.reviewMatch.emit(row);
  }

  onComments(row: TransactionLedgerRow, event: Event): void {
    event.stopPropagation();
    this.commentsOpen.emit(row);
  }

  onAttachment(row: TransactionLedgerRow, event: Event): void {
    event.stopPropagation();
    this.attachmentOpen.emit(row);
  }

  isRowCoding(row: TransactionLedgerRow): boolean {
    return !!this.codingIds && this.codingIds.size > 0
      && this.codingIds.has(rowKey(row.unreconciledReportId));
  }

  // ── Selection ──────────────────────────────────────────────

  isSelected(row: TransactionLedgerRow): boolean {
    return !!this.selected[row.unreconciledReportId];
  }

  toggleSelect(row: TransactionLedgerRow, event?: Event): void {
    if (event) { event.stopPropagation(); }

    const next = { ...this.selected };
    if (next[row.unreconciledReportId]) {
      delete next[row.unreconciledReportId];
    } else {
      next[row.unreconciledReportId] = true;
    }
    this.selectedChange.emit(next);
  }

  /** Only the rows actually loaded can be selected, so "all" means all on screen. */
  get allSelected(): boolean {
    const count = Object.keys(this.selected).filter(id => this.selected[id]).length;
    return this.rows.length > 0 && count === this.rows.length;
  }

  toggleSelectAll(): void {
    if (this.allSelected) {
      this.selectedChange.emit({});
      return;
    }

    const next: { [id: string]: boolean } = {};
    this.rows.forEach(r => { next[r.unreconciledReportId] = true; });
    this.selectedChange.emit(next);
  }

  // ── Row presentation ───────────────────────────────────────

  /**
   * What to show as the transaction's name.
   *
   * An unmatched row has nothing but the bank's narration, and we must not dress that
   * up as a supplier: the narration is shown as-is, on its own. Only once a row is
   * matched to an invoice does relatedName give us a real supplier to show, with the
   * narration as its sub-line.
   */
  supplierName(row: TransactionLedgerRow): string | null {
    return row.relatedName && row.relatedName.trim() ? row.relatedName.trim() : null;
  }

  isIdentified(row: TransactionLedgerRow): boolean {
    return !!this.supplierName(row);
  }

  /**
   * SD-1254: the supplier the Coding Assistant picked for an unmatched row it coded. A real
   * supplier, not the narration, but only suggested: the row says so until a person confirms it.
   */
  suggestedSupplierName(row: TransactionLedgerRow): string | null {
    const match = this.supplierMatch(row);
    const name = (row.agentCoding?.contactName || '').trim();
    return name && (match === 'matched' || match === 'possible') ? name : null;
  }

  /**
   * The Coding Assistant's verdict on the supplier, for an unmatched row with a suggestion on show:
   * 'matched' (one of the organisation's, sure), 'possible' (one of its, guessed), 'new' (none of its:
   * a new supplier) or 'none'. Null when there is no suggestion to speak of.
   */
  supplierMatch(row: TransactionLedgerRow): 'matched' | 'possible' | 'new' | 'none' | null {
    if (!row.agentCoding || !this.suggestedCoding.isVisible || this.isIdentified(row) || this.isReconciledRow(row)) {
      return null;
    }
    return SuggestedCodingService.supplierMatch(row.agentCoding);
  }

  /** A suggestion's new supplier: the name the AI read, which none of the organisation's suppliers has. */
  newSupplierName(row: TransactionLedgerRow): string | null {
    const name = (row.agentCoding?.newSupplierName || '').trim();
    return this.supplierMatch(row) === 'new' && name ? name : null;
  }

  /** The name to look a logo up by: none for a new supplier, which has no record to have one. */
  tileLogoName(row: TransactionLedgerRow): string | null {
    return this.newSupplierName(row) ? null : this.tileSupplierName(row);
  }

  /** Accept as is: only a suggestion whose supplier is one of the organisation's, and sure. */
  canAccept(row: TransactionLedgerRow): boolean {
    return SuggestedCodingService.canAccept(row);
  }

  /** A row whose Coding Assistant coding is waiting for a person, whether or not it is on show. */
  hasPendingSuggestion(row: TransactionLedgerRow): boolean {
    return !this.readOnly && !!row.agentCoding && !row.hasSuggestedMatch
      && !this.isReconciledRow(row) && !this.isRowCoding(row);
  }

  /** The suggestion as it shows on the row: pending, and the user has suggestions on. */
  showsSuggestion(row: TransactionLedgerRow): boolean {
    return this.suggestedCoding.isVisible && this.hasPendingSuggestion(row);
  }

  /** Every loaded row with a suggestion waiting, for the strip above the table. */
  get pendingSuggestions(): TransactionLedgerRow[] {
    return (this.rows || []).filter(row => this.hasPendingSuggestion(row));
  }

  /** Those that can be accepted as they are: Accept all takes these. */
  get acceptableSuggestions(): TransactionLedgerRow[] {
    return this.pendingSuggestions.filter(row => this.canAccept(row));
  }

  onAcceptSuggestion(row: TransactionLedgerRow, event: Event): void {
    event.stopPropagation();
    this.acceptSuggestions.emit([row]);
  }

  acceptAllSuggestions(): void {
    const rows = this.acceptableSuggestions;
    if (rows.length) { this.acceptSuggestions.emit(rows); }
  }

  /** The supplier on the row's tile: the matched one, else the suggested one, else the new one. */
  tileSupplierName(row: TransactionLedgerRow): string | null {
    return this.supplierName(row) || this.suggestedSupplierName(row) || this.newSupplierName(row);
  }

  /** SD-1240: the suggested supplier's id, so its logo is found by id rather than by name. */
  tileSupplierId(row: TransactionLedgerRow): string | null {
    return this.suggestedSupplierName(row) ? (row.agentCoding?.contactId || null) : null;
  }

  rowTitle(row: TransactionLedgerRow): string {
    return this.tileSupplierName(row) || row.description || '—';
  }

  /** The narration under a matched or suggested supplier; nothing for a row with neither. */
  rowSubtitle(row: TransactionLedgerRow): string {
    return this.tileSupplierName(row) ? (row.description || '') : '';
  }

  initials(row: TransactionLedgerRow): string {
    const name = this.tileSupplierName(row) || '';
    const parts = name.replace(/[^A-Za-z0-9 ]/g, '').split(' ').filter(Boolean);
    return (parts.slice(0, 2).map(p => p[0]).join('') || '?').toUpperCase();
  }

  isReconciledRow(row: TransactionLedgerRow): boolean {
    return isReconciledRow(row);
  }

  /**
   * The two words the filter pills above the list use, and nothing else.
   *
   * A row used to read "Match found" or "Needs review", neither of which is a state the list
   * can be filtered to -- so a row said one thing and the pill that contained it said another.
   * Whether a suggestion exists is still worth knowing, so it keeps its own colour and says so
   * on hover; it is a shade of "to reconcile" rather than a third state.
   */
  statusLabel(row: TransactionLedgerRow): string {
    return this.isReconciledRow(row) ? 'Reconciled' : 'To reconcile';
  }

  /**
   * Two states, two colours. A suggested match used to give the chip a third, blue one -- so
   * two rows both reading "To reconcile" were coloured differently, which says they are in
   * different states when they are not. The suggestion has its own "Match found" chip in the
   * Coded to column; the status column answers only whether the row is done.
   */
  statusClass(row: TransactionLedgerRow): string {
    return this.isReconciledRow(row) ? 'tx-chip--done' : 'tx-chip--review';
  }

  /** What the chip's colour is saying, for anyone who cannot read colour alone. */
  statusTitle(row: TransactionLedgerRow): string {
    return this.isReconciledRow(row) ? 'Reconciled' : 'To reconcile';
  }

  /** "6009 · Office Maintenance", or just the name where the account has no code. */
  /** SD-1254: the account the Coding Assistant coded the row to, in Synced only. */
  agentCodingLabel(row: TransactionLedgerRow): string {
    const c = row.agentCoding;
    if (!c) { return ''; }
    const code = (c.accountCode || '').trim();
    const name = (c.accountName || '').trim();
    return (code && name ? `${code} · ${name}` : (name || code || 'Coded'));
  }

  /** Under the suggested coding: how sure the Coding Assistant was, when it said. */
  agentCodingNote(row: TransactionLedgerRow): string {
    const c = row.agentCoding;
    return c && c.confidence != null ? `${Math.round(c.confidence)}% confident` : '';
  }

  agentCodingTitle(row: TransactionLedgerRow): string {
    const c = row.agentCoding;
    if (!c) { return ''; }
    return 'Coded by the Coding Assistant'
      + (c.confidence != null ? ` at ${Math.round(c.confidence)}%` : '')
      + (c.contactName ? `, supplier ${c.contactName}` : '')
      + (c.reason ? `: ${c.reason}` : '')
      + '. Nothing has been sent to your accounting system yet.';
  }

  codedLabel(row: TransactionLedgerRow): string {
    const code = (row.codedAccountCode || '').trim();
    const name = (row.codedAccountName || '').trim();
    return code && name ? `${code} · ${name}` : (name || code);
  }

  /**
   * Row dates in the organisation's order, which is the only thing separating 01/02 from
   * 02/01. Two-digit year, because a statement can hold several years at once and a column
   * is too narrow for four digits.
   */
  displayDate(value: string | null): string {
    const format = this._organizationService.selectedOrganizationDateFormat === 'en-US'
      ? 'MM/DD/YY'
      : 'DD/MM/YY';
    return value ? moment(value).format(format) : '—';
  }

  money(value: number | null): string {
    return formatMoney(value);
  }

  signed(value: number | null): string {
    return formatSigned(value);
  }

  // ── Scrolling ──────────────────────────────────────────────

  /**
   * Asks for the next block when the user nears the bottom. The server pages and the list
   * appends, so the DOM only ever holds what has actually been scrolled to.
   */
  onTableScroll(event: Event): void {
    const el = event.target as HTMLElement;
    if (!el) { return; }

    const remaining = el.scrollHeight - el.scrollTop - el.clientHeight;
    if (remaining < 240) { this.loadMore.emit(); }
  }

  trackByGroup(_: number, group: TransactionDayGroup): string {
    return group.key;
  }

  trackByRow(_: number, row: TransactionLedgerRow): string {
    return row.unreconciledReportId;
  }
}
